import { HttpClient } from "@angular/common/http";
import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from "@angular/core";
import { ControlValueAccessor, UntypedFormBuilder, UntypedFormGroup, NG_VALUE_ACCESSOR } from "@angular/forms";
import { CacheService } from "@delon/cache";
import { TranslateService } from "@root/shared/service/translate.service";

export const V4_HOUSES_SELECT_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => V4HousesSelectComponent),
  multi: true,
};

@Component({
  selector: "v4-house-select",
  templateUrl: "./houses-select.component.html",
  styleUrls: ["./houses-select.component.scss"],
  providers: [V4_HOUSES_SELECT_VALUE_ACCESSOR],
})
export class V4HousesSelectComponent implements OnInit, ControlValueAccessor {
  readonly listApi = "/api/house/search";
  @Input() mode: "single" | "multi" = "multi";
  @Input() size: "default" | "large" = "default";
  @Input() placeholder = "word.house_singular";
  @Input() showDeleted = true;

  /**
   *
   * 下面的属性主要用于非form表单的场景
   * 比如独立使用该组件，仅关注选中事件，而不关注值本身的情况下，可以使用这个属性
   * Output返回的是Option[]，而不是number[]，是因为在独立使用该组件的情况下，可能需要获取更多的信息，因此将Option[]返回，而不是仅仅返回id
   *
   * 对于form表单场景，使用 formControlName 或者 ngModel 即可，无需关注这个属性
   *
   */
  @Output() onSelectValues = new EventEmitter<Option[]>();
  selectedValues: number[];

  dropdownVisible = false;
  listOfData: Option[] = [];
  listOfExtraData: Option[] = [];
  form: UntypedFormGroup = this.fb.group({
    group_id: null,
    house_type_id: null,
    house_tag_id: null,
  });

  showTextItem = false;
  unfoldExtra = false;
  listOfGroupOption: Option[];
  listOfHouseTypeOption: Option[];
  listOfTagOption: Option[];
  get textItem() {
    if (this.mode === "multi") {
      return this.t.select(
        ["component.search.house_numbers_singular", "component.search.house_numbers"],
        "len",
        this.selectedValues?.length,
      );

      // const len = this.selectedValues?.length;
      // if (len > 1) {
      //   return this.t.t('component.search.house_numbers', { len });
      // } else {
      //   return this.t.t('component.search.house_numbers_singular', { len });
      // }
    } else {
      return this.listOfData?.find((item) => item.value === this.selectedValues?.[0])?.label;
    }
  }

  get groupId() {
    return this.form.value.group_id;
  }

  get roomTypeId() {
    return this.form.value.house_type_id;
  }

  get tagId() {
    return this.form.value.house_tag_id;
  }

  _registerOnChange: (value: any) => void = (v: any) => {};
  constructor(
    private http: HttpClient,
    private fb: UntypedFormBuilder,
    private t: TranslateService,
    private cache: CacheService,
  ) {}

  writeValue(value: any | any[]): void {
    console.log("v4-house-select write value: ", value);
    if (!value || value?.length === 0) {
      this.selectedValues = null;
      this.showTextItem = false;
    } else {
      if (!Array.isArray(value)) {
        /** 这里强制转成数字，因为上层业务很多时候会传递字符串格式的 id，导致无法找到选项 */
        this.selectedValues = [+value];
      } else {
        this.selectedValues = value?.map((i) => +i);
      }
      this.showTextItem = true;
    }
  }

  registerOnChange(fn: any): void {
    this._registerOnChange = fn;
  }
  registerOnTouched(fn: any): void {}
  setDisabledState?(isDisabled: boolean): void {}

  ngOnInit() {
    this.fetchGroupList();
    this.fetchHouseTypeList();
    this.fetchTagList();

    this.fetchDataList();

    this.subscribeFormChange();
  }

  private subscribeFormChange() {
    this.form.valueChanges.subscribe(() => {
      this.fetchDataList();
    });
  }

  fetchDataList() {
    const value = this.form.value;
    const params = {
      ...value,
      page: 1,
      page_size: 10000,
      type: "include_delete",
    };
    if (value.house_tag_id) {
      Object.assign(params, {
        house_tag_type: "include",
        house_tag_id: JSON.stringify([value.house_tag_id]),
      });
    }
    this.http.get(this.listApi, { params }).subscribe((rst: any) => {
      this.listOfData = rst?.data?.list
        ?.filter((item) => !item.delete_time)
        ?.map((item) => ({
          label: item.house_title,
          value: item.id,
        }));
      this.listOfExtraData = rst?.data?.list
        ?.filter((item) => item.delete_time)
        ?.map((item) => ({
          label: item.house_title,
          value: item.id,
        }));
    });
  }

  private fetchGroupList() {
    return this.cache
      .get("/api/house_group/group_list", { mode: "promise", type: "m", expire: 1 * 60 })
      .subscribe((rst: any) => {
        const list = rst?.data?.list ?? [];
        this.listOfGroupOption = list.map((item) => ({
          label: item.name,
          value: item.group_id,
        }));
      });
  }

  private fetchHouseTypeList() {
    return this.cache
      .get("/api/house_type/all", { mode: "promise", type: "m", expire: 1 * 60 })
      .subscribe((rst: any) => {
        const list = rst?.data?.list ?? [];
        this.listOfHouseTypeOption = list.map((item) => ({
          label: item.title,
          value: item.id,
        }));
      });
  }

  private fetchTagList() {
    return this.cache
      .get("/api/house_status_setting/get", { mode: "promise", type: "m", expire: 1 * 60 })
      .subscribe((rst: any) => {
        const list = rst?.data?.house_tag ?? [];
        this.listOfTagOption = list.map((item) => ({
          label: item.name,
          value: item.id,
        }));
      });
  }

  private emitValues() {
    if (this.mode === "multi") {
      this._registerOnChange(this.selectedValues);
    } else {
      this._registerOnChange(this.selectedValues?.[0]);
    }
  }

  /** 仅用于单选模式，多选模式的交互需要确认，不需要响应select事件 */
  onSelect(option: Option[]) {
    console.log("on select: ", option);

    if (this.mode === "single") {
      this.onConfirm(option);
    }
  }

  onConfirm(options: Option[]) {
    const values = options.map((opt) => opt.value);
    this.selectedValues = values;
    this.showTextItem = values?.length !== 0;

    this.onSelectValues.emit(options);
    this.emitValues();

    this.dropdownVisible = false;
    // this.modalRef.destroy(options);
  }

  onCancel() {
    this.dropdownVisible = false;
    // this.modalRef.destroy();
  }

  onReset() {
    this.form.reset();
    this.selectedValues = [];
    this.showTextItem = false;

    if (this.mode === "single") {
      this.emitValues();
      this.dropdownVisible = false;
    }
  }
}
